import { inject, Injectable } from '@angular/core';
import { Store, StoreConfig } from '@datorama/akita';
import { map, Observable, tap } from 'rxjs';

import {
  GqlService,
  listDriverCustomDistributionQuery,
  listDriverCustomGroupsQuery,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';

export type CustomCurve = Partial<listDriverCustomGroupsQuery>;

export interface CustomCurvesState {
  currentDistributions: listDriverCustomDistributionQuery[];
  curves: CustomCurve[];
  curveToCreateOrEdit: CustomCurve | null;
  selectedCurve: CustomCurve | null;
}

export const customCurvesInitialState = (): CustomCurvesState => {
  return {
    currentDistributions: [],
    curves: [],
    curveToCreateOrEdit: null,
    selectedCurve: null,
  };
};

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'custom-curves' })
export class CustomCurvesStore extends Store<CustomCurvesState> {
  private readonly gqlService = inject(GqlService);
  private readonly overlayService = inject(OverlayService);

  constructor() {
    super(customCurvesInitialState());
  }

  getCustomDriverGroups(): Observable<listDriverCustomGroupsQuery[]> {
    return this.gqlService.listDriverCustomGroups$().pipe(
      tap((listGroupsResponse) => {
        if (listGroupsResponse.errors?.length) {
          this.overlayService.error(listGroupsResponse.errors);
        } else {
          this.updatePartialy({
            curves: listGroupsResponse.data || [],
          });
        }
      }),
      map((listGroupsResponse) => listGroupsResponse.data || [])
    );
  }

  getDriverCustomDistribution(): Observable<listDriverCustomDistributionQuery[]> {
    return this.gqlService
      .listDriverCustomDistribution$(this.getValue().selectedCurve?.custom_group_id as string)
      .pipe(
        tap((listDistributionsResponse) => {
          if (listDistributionsResponse.errors?.length) {
            this.overlayService.error(listDistributionsResponse.errors);
          } else {
            this.updatePartialy({
              currentDistributions: listDistributionsResponse.data || [],
            });
          }
        }),
        map((listDistributionsResponse) => listDistributionsResponse.data || [])
      );
  }

  updatePartialy(partial: Partial<CustomCurvesState>): void {
    this.update((state) => ({
      ...state,
      ...partial,
    }));
  }
}
